import { FC, type RefObject, useRef, useState } from 'react';
import type { Interpolation, Theme } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { TFunction, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton } from '@frontend/design-system';
import { voiceAiActivityAccordionStyles as styles } from './styles';
import { ResolvedVoiceAiActivity } from './types';

type VoiceAiActivityAccordionProps = {
  topActivity: ResolvedVoiceAiActivity | null;
  activities: ResolvedVoiceAiActivity[];
  contactName?: string;
  attachedToCallBubble?: boolean;
  scrollTargetRef?: RefObject<HTMLElement | null>;
};

type ActivityIconProps = Pick<
  ResolvedVoiceAiActivity,
  'iconName' | 'iconColor' | 'customIconColor' | 'iconGradientBg'
> & {
  size?: number;
  iconCss?: Interpolation<Theme>;
};

const ActivityIcon: FC<ActivityIconProps> = ({
  iconName,
  iconColor,
  customIconColor,
  iconGradientBg,
  size = 14,
  iconCss,
}) => (
  <span css={styles.iconWrapper(customIconColor, iconGradientBg)}>
    <Icon name={iconName} size={size} css={[iconCss, { color: customIconColor ? undefined : iconColor }]} />
  </span>
);

const getExpandedActivityText = ({
  activity,
  contactName,
  t,
}: {
  activity: ResolvedVoiceAiActivity;
  contactName?: string;
  t: TFunction<'thread-body', undefined>;
}) => {
  const personName = contactName?.trim();

  switch (activity.key) {
    case 'added_to_schedule':
      return personName ? t('{{name}} was added to schedule', { name: personName }) : activity.label;
    case 'rescheduled':
      return personName ? t("{{name}}'s appointment was rescheduled", { name: personName }) : activity.label;
    case 'cancelled':
      return personName ? t("{{name}}'s appointment was cancelled", { name: personName }) : activity.label;
    case 'confirmed':
      return personName ? t("{{name}}'s appointment was confirmed", { name: personName }) : activity.label;
    default:
      return activity.label;
  }
};

export const VoiceAiActivityAccordion: FC<VoiceAiActivityAccordionProps> = ({
  topActivity,
  activities,
  contactName,
  attachedToCallBubble = false,
  scrollTargetRef,
}) => {
  const { t } = useTranslation('thread-body');
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  if (!topActivity || !activities.length) return null;

  if (activities.length === 1) {
    return (
      <div data-testid='voice-ai-activity-accordion' css={styles.container(attachedToCallBubble)}>
        <div data-testid='voice-ai-activity-static' css={styles.staticTrigger}>
          <ActivityIcon
            iconName={topActivity.iconName}
            iconColor={topActivity.iconColor}
            customIconColor={topActivity.customIconColor}
            iconGradientBg={topActivity.iconGradientBg}
          />
          <span data-testid='voice-ai-activity-collapsed-label' css={styles.collapsedLabel}>
            {topActivity.label}
          </span>
        </div>
      </div>
    );
  }

  const hiddenCount = activities.length - 1;

  return (
    <div ref={containerRef} data-testid='voice-ai-activity-accordion' css={styles.container(attachedToCallBubble)}>
      <NakedButton
        type='button'
        data-testid='voice-ai-activity-accordion-trigger'
        onClick={() => setIsExpanded((prev) => !prev)}
        css={styles.trigger}
      >
        {isExpanded ? (
          <>
            <Icon name='aiweave-assistant' size={14} css={{ color: theme.colors.neutral90 }} />
            <span data-testid='voice-ai-activity-expanded-label' css={styles.collapsedLabel}>
              {t('AI Receptionist Call Activity')}
            </span>
          </>
        ) : (
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <ActivityIcon
              iconName={topActivity.iconName}
              iconColor={topActivity.iconColor}
              customIconColor={topActivity.customIconColor}
              iconGradientBg={topActivity.iconGradientBg}
            />
            <span data-testid='voice-ai-activity-collapsed-label' css={styles.collapsedLabel}>
              {topActivity.label}
            </span>
            {hiddenCount > 0 && (
              <span data-testid='voice-ai-activity-hidden-count' css={styles.hiddenCount}>
                +{hiddenCount}
              </span>
            )}
          </div>
        )}
        <Icon name='caret-down-small' size={14} css={styles.chevron(isExpanded)} />
      </NakedButton>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            data-testid='voice-ai-activity-expanded-body'
            css={styles.expandedBody}
            initial='collapsed'
            animate='open'
            exit='collapsed'
            variants={{
              open: { height: 'auto', opacity: 1 },
              collapsed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            onAnimationComplete={(definition) => {
              if (definition !== 'open') return;

              const scrollTarget = scrollTargetRef?.current ?? containerRef.current;

              scrollTarget?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }}
          >
            <div css={styles.expandedBodyContent}>
              {activities.map((activity, index) => {
                const activityText = getExpandedActivityText({ activity, contactName, t });
                const activityRowKey = activity.createdAt ?? `${activity.key}-${activity.index}`;

                return (
                  <div key={activityRowKey} data-testid={`voice-ai-activity-row-${index}`} css={styles.row}>
                    <ActivityIcon
                      iconName={activity.iconName}
                      iconColor={activity.iconColor}
                      customIconColor={activity.customIconColor}
                      iconGradientBg={activity.iconGradientBg}
                    />
                    <span css={styles.rowText}>{activityText}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
